/* ==========================================================================
   SERPROMAQ - MOTOR DINÁMICO E INTERACTIVO DEL CATÁLOGO COMERCIAL
   ========================================================================== */

import { getLoadedCatalogItems, subscribeToCatalog } from './catalog-service.js';
import { openQuoteModal } from './quote-modal.js';

let activeCatalogItems = getLoadedCatalogItems();
let currentFilter = 'todos';
let currentSearchQuery = '';
let currentSlideIndex = 0;
let itemsPerPage = 3;

function calculateItemsPerPage() {
  if (window.innerWidth < 768) {
    itemsPerPage = 1;
  } else if (window.innerWidth < 1200) {
    itemsPerPage = 2;
  } else {
    itemsPerPage = 3;
  }
}

export function initCatalog() {
  const catalogGrid = document.getElementById('catalogGrid');
  const catalogTabs = document.querySelectorAll('.catalog-tab');
  const searchInput = document.getElementById('catalogSearchInput');
  const prevBtn = document.getElementById('catalogPrevBtn');
  const nextBtn = document.getElementById('catalogNextBtn');
  const paginationInfo = document.getElementById('catalogPaginationInfo');
  const paginationDots = document.getElementById('catalogDots');

  if (!catalogGrid) return;

  calculateItemsPerPage();
  window.addEventListener('resize', () => {
    calculateItemsPerPage();
    renderCatalog();
  });

  // Suscribirse a cambios en tiempo real desde Firestore / Servicio
  subscribeToCatalog((liveItems, isFirestore) => {
    if (Array.isArray(liveItems)) {
      activeCatalogItems = liveItems;
      updateTabCounts();
      renderCatalog(false);
    }
  });

  // Actualizar contadores dinámicos en las pestañas
  updateTabCounts();

  function getFilteredItems() {
    return activeCatalogItems.filter(item => {
      const matchesCategory = currentFilter === 'todos' || item.category === currentFilter;
      
      if (!currentSearchQuery) return matchesCategory;

      const query = currentSearchQuery.toLowerCase();
      const matchesTitle = item.title.toLowerCase().includes(query);
      const matchesDesc = (item.description || '').toLowerCase().includes(query);
      const matchesBadge = (item.badgeText || '').toLowerCase().includes(query);
      const matchesTag = item.highlightTag ? item.highlightTag.toLowerCase().includes(query) : false;
      const matchesSpecs = item.specs ? Object.values(item.specs).some(val => (val || '').toLowerCase().includes(query)) : false;

      return matchesCategory && (matchesTitle || matchesDesc || matchesBadge || matchesTag || matchesSpecs);
    });
  }

  function updateTabCounts() {
    catalogTabs.forEach(tab => {
      const filter = tab.getAttribute('data-filter');
      const count = filter === 'todos' 
        ? activeCatalogItems.length 
        : activeCatalogItems.filter(item => item.category === filter).length;
      
      const countBadge = tab.querySelector('.catalog-tab-count');
      if (countBadge) {
        countBadge.textContent = count;
      }
    });
  }

  function renderCatalog(animate = true) {
    const filtered = getFilteredItems();
    const totalItems = filtered.length;
    const maxPages = Math.ceil(totalItems / itemsPerPage);

    if (currentSlideIndex >= maxPages) {
      currentSlideIndex = Math.max(0, maxPages - 1);
    }

    const startIndex = currentSlideIndex * itemsPerPage;
    const currentBatch = filtered.slice(startIndex, startIndex + itemsPerPage);

    // Actualizar controles de paginación
    if (paginationInfo) {
      if (totalItems === 0) {
        paginationInfo.innerHTML = 'Sin resultados';
      } else {
        const startNum = startIndex + 1;
        const endNum = Math.min(startIndex + itemsPerPage, totalItems);
        paginationInfo.innerHTML = `Mostrando <strong>${startNum}-${endNum}</strong> de <strong>${totalItems}</strong> soluciones`;
      }
    }

    if (prevBtn) prevBtn.disabled = currentSlideIndex === 0;
    if (nextBtn) nextBtn.disabled = currentSlideIndex >= maxPages - 1 || maxPages <= 1;

    // Renderizar Puntos Indicadores
    if (paginationDots) {
      paginationDots.innerHTML = '';
      if (maxPages > 1) {
        for (let i = 0; i < maxPages; i++) {
          const dot = document.createElement('button');
          dot.className = `catalog-dot-indicator ${i === currentSlideIndex ? 'active' : ''}`;
          dot.setAttribute('aria-label', `Página ${i + 1}`);
          dot.addEventListener('click', () => {
            currentSlideIndex = i;
            renderCatalog(true);
          });
          paginationDots.appendChild(dot);
        }
      }
    }

    // Renderizar Contenido
    if (totalItems === 0) {
      catalogGrid.innerHTML = `
        <div class="catalog-empty-state">
          <div class="catalog-empty-icon">
            <i class="fa-solid fa-magnifying-glass"></i>
          </div>
          <h3>No encontramos resultados para "${currentSearchQuery}"</h3>
          <p>Intenta con palabras clave como <em>Litio, 3.5 Tn, Alquiler, Mantenimiento, Llantas</em> o limpia los filtros.</p>
          <button class="btn btn-outline" id="btnResetCatalogFilters" style="margin-top: 1rem;">
            <i class="fa-solid fa-rotate-left"></i> Restablecer Filtros
          </button>
        </div>
      `;

      const resetBtn = document.getElementById('btnResetCatalogFilters');
      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          currentFilter = 'todos';
          currentSearchQuery = '';
          if (searchInput) searchInput.value = '';
          catalogTabs.forEach(t => t.classList.toggle('active', t.getAttribute('data-filter') === 'todos'));
          currentSlideIndex = 0;
          renderCatalog(true);
        });
      }
      return;
    }

    catalogGrid.innerHTML = '';

    currentBatch.forEach((item, index) => {
      const card = document.createElement('div');
      card.className = `catalog-card ${animate ? 'animate-card-in' : ''}`;
      card.style.animationDelay = `${index * 0.08}s`;

      const categoryLabel = item.category === 'venta' ? 'VENTA DE EQUIPO' 
        : item.category === 'alquiler' ? 'ALQUILER FLOTA' 
        : item.category === 'mantenimiento' ? 'TALLER & SERVICIO' 
        : 'REPUESTO / INSUMO';

      const specEntries = Object.entries(item.specs).slice(0, 4);

      card.innerHTML = `
        <div class="catalog-card-glow-bg"></div>
        
        <!-- Header Multimedia del Producto -->
        <div class="catalog-img-wrapper">
          <img src="${item.image}" alt="${item.title}" loading="lazy" />
          <div class="catalog-img-overlay">
            <button type="button" class="btn-catalog-zoom" data-item-id="${item.id}" title="Ver Ficha Técnica Completa">
              <i class="fa-solid fa-eye"></i> Vista Rápida
            </button>
          </div>
          
          <span class="catalog-badge ${item.badgeClass}">
            <i class="fa-solid fa-star"></i> ${item.badgeText}
          </span>
          
          <span class="catalog-availability-pill">
            <span class="pulse-dot"></span> ${item.availability || 'Disponible en Stock'}
          </span>
        </div>

        <!-- Información y Especificaciones del Producto -->
        <div class="catalog-body">
          <div class="catalog-meta-top">
            <span class="catalog-category-tag">${categoryLabel}</span>
            <span class="catalog-highlight-tag">${item.highlightTag || 'Garantía SERPROMAQ'}</span>
          </div>

          <h3 class="catalog-title" title="${item.title}">${item.title}</h3>
          
          <p class="catalog-desc">
            ${item.description}
          </p>

          <!-- Especificaciones Clave con Iconos Modernos -->
          <div class="catalog-specs-matrix">
            ${specEntries.map(([key, val]) => `
              <div class="spec-matrix-item">
                <div class="spec-icon-box">
                  <i class="${getSpecIcon(key, val)}"></i>
                </div>
                <div class="spec-text-box">
                  <span class="spec-label">${key}</span>
                  <strong class="spec-value" title="${val}">${val}</strong>
                </div>
              </div>
            `).join('')}
          </div>

          <!-- Botones de Acción de Alta Conversión -->
          <div class="catalog-action-row">
            <button type="button" class="btn-catalog-details" data-item-id="${item.id}">
              <i class="fa-solid fa-file-lines"></i> Ficha
            </button>
            <button type="button" class="btn-catalog-quote btn-quote-item" data-item-title="${item.title}" data-item-category="${item.category}">
              <i class="fa-brands fa-whatsapp"></i> Cotizar
            </button>
          </div>
        </div>
      `;

      catalogGrid.appendChild(card);
    });

    // Vincular Eventos de Botones de Cotización vía WhatsApp
    catalogGrid.querySelectorAll('.btn-quote-item').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const title = btn.getAttribute('data-item-title');
        const category = btn.getAttribute('data-item-category');
        const text = encodeURIComponent(`👋 *Hola SERPROMAQ!* Deseo cotizar el siguiente producto/servicio:\n\n🚜 *${title}*\n📁 *Categoría:* ${(category || '').toUpperCase()}\n\n¿Me pueden brindar cotización, precio y disponibilidad?`);
        window.open(`https://wa.me/573168275829?text=${text}`, '_blank');
      });
    });

    // Vincular Eventos de Ficha Rápida / Zoom
    catalogGrid.querySelectorAll('.btn-catalog-zoom, .btn-catalog-details').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const itemId = btn.getAttribute('data-item-id');
        openCatalogQuickView(itemId);
      });
    });
  }

  function getSpecIcon(key, value) {
    const k = (key || '').toLowerCase();
    const v = (value || '').toLowerCase();
    if (k.includes('capacidad') || k.includes('peso') || k.includes('carga')) return 'fa-solid fa-weight-hanging';
    if (k.includes('altura') || k.includes('torre') || k.includes('mástil') || k.includes('mastil')) return 'fa-solid fa-arrows-up-down';
    if (k.includes('motor') || k.includes('batería') || k.includes('bateria') || k.includes('voltaje')) {
      if (v.includes('litio') || v.includes('eléctrico') || v.includes('electrico') || v.includes('ac')) return 'fa-solid fa-bolt-lightning';
      return 'fa-solid fa-gas-pump';
    }
    if (k.includes('garantía') || k.includes('garantia') || k.includes('cobertura')) return 'fa-solid fa-shield-halved';
    if (k.includes('duración') || k.includes('duracion') || k.includes('tiempo')) return 'fa-solid fa-clock';
    if (k.includes('tipo') || k.includes('sistema')) return 'fa-solid fa-sliders';
    if (k.includes('compatibilidad')) return 'fa-solid fa-truck-moving';
    if (k.includes('despacho') || k.includes('entrega')) return 'fa-solid fa-truck-fast';
    return 'fa-solid fa-circle-check';
  }

  // Event Listeners de Filtros por Pestaña
  catalogTabs.forEach(tab => {
    tab.addEventListener('click', (e) => {
      catalogTabs.forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      currentFilter = tab.getAttribute('data-filter');
      currentSlideIndex = 0;
      renderCatalog(true);
    });
  });

  // Event Listener del Buscador con debounce y botón limpiar
  const searchClearBtn = document.getElementById('catalogSearchClear');
  if (searchInput) {
    let debounceTimer = null;
    searchInput.addEventListener('input', (e) => {
      const val = e.target.value;
      if (searchClearBtn) {
        searchClearBtn.style.display = val.length > 0 ? 'inline-flex' : 'none';
      }
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        currentSearchQuery = val.trim();
        currentSlideIndex = 0;
        renderCatalog(true);
      }, 200);
    });

    if (searchClearBtn) {
      searchClearBtn.addEventListener('click', () => {
        searchInput.value = '';
        searchClearBtn.style.display = 'none';
        currentSearchQuery = '';
        currentSlideIndex = 0;
        renderCatalog(true);
        searchInput.focus();
      });
    }
  }

  // Controles de Paginación / Carrusel
  if (prevBtn) {
    prevBtn.addEventListener('click', () => {
      if (currentSlideIndex > 0) {
        currentSlideIndex--;
        renderCatalog(true);
      }
    });
  }

  if (nextBtn) {
    nextBtn.addEventListener('click', () => {
      const maxPages = Math.ceil(getFilteredItems().length / itemsPerPage);
      if (currentSlideIndex < maxPages - 1) {
        currentSlideIndex++;
        renderCatalog(true);
      }
    });
  }

  // Inicializar Modal de Ficha Técnica
  initCatalogQuickViewModal();

  // Render inicial
  renderCatalog(false);
}

/**
 * Modal de Ficha Técnica Detallada (Quick View)
 */
function initCatalogQuickViewModal() {
  const modal = document.getElementById('catalogQuickModal');
  const closeBtn = document.getElementById('catalogQuickClose');
  if (!modal) return;

  if (closeBtn) {
    closeBtn.addEventListener('click', () => {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    });
  }

  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && modal.classList.contains('active')) {
      modal.classList.remove('active');
      document.body.style.overflow = '';
    }
  });
}

export function openCatalogQuickView(itemId) {
  const item = activeCatalogItems.find(i => i.id === itemId);
  const modal = document.getElementById('catalogQuickModal');
  const content = document.getElementById('catalogQuickContent');

  if (!item || !modal || !content) return;

  content.innerHTML = `
    <div class="quickview-grid">
      <div class="quickview-media">
        <img src="${item.image}" alt="${item.title}" class="quickview-img" />
        <span class="quickview-badge ${item.badgeClass}">${item.badgeText}</span>
        <div class="quickview-stock-tag">
          <span class="pulse-dot"></span> ${item.availability}
        </div>
      </div>

      <div class="quickview-info">
        <span class="quickview-category">${item.category.toUpperCase()} • SERPROMAQ</span>
        <h2 class="quickview-title">${item.title}</h2>
        <p class="quickview-desc">${item.description}</p>

        <h4 class="quickview-section-title"><i class="fa-solid fa-list-check"></i> Especificaciones Técnicas:</h4>
        <div class="quickview-specs-list">
          ${Object.entries(item.specs).map(([k, v]) => `
            <div class="quickview-spec-row">
              <span class="spec-k">${k}:</span>
              <strong class="spec-v">${v}</strong>
            </div>
          `).join('')}
        </div>

        ${item.features && item.features.length > 0 ? `
          <h4 class="quickview-section-title" style="margin-top: 1rem;"><i class="fa-solid fa-circle-check" style="color: var(--color-primary);"></i> Ventajas Destacadas:</h4>
          <ul class="quickview-features-list">
            ${item.features.map(f => `<li>${f}</li>`).join('')}
          </ul>
        ` : ''}

        <div class="quickview-actions">
          <button type="button" class="btn btn-whatsapp btn-quick-whatsapp" data-title="${item.title}" style="width: 100%; font-weight: 800; font-size: 0.95rem; justify-content: center; padding: 0.8rem 1.25rem;">
            <i class="fa-brands fa-whatsapp"></i> Cotizar por WhatsApp
          </button>
        </div>
      </div>
    </div>
  `;

  // Bind WhatsApp in quickview
  const waBtn = content.querySelector('.btn-quick-whatsapp');
  if (waBtn) {
    waBtn.addEventListener('click', () => {
      const text = encodeURIComponent(`👋 *Hola SERPROMAQ!* Deseo cotizar el siguiente producto/servicio desde la Ficha Técnica:\n\n🚜 *${item.title}*\n📁 *Categoría:* ${item.category.toUpperCase()}\n\n¿Me pueden brindar cotización, precio y disponibilidad?`);
      window.open(`https://wa.me/573168275829?text=${text}`, '_blank');
    });
  }

  modal.classList.add('active');
  document.body.style.overflow = 'hidden';
}
